import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

const AuthCtx = createContext(null);

// Browser-native requests (<img>, PDF links) can't send the X-Venue-Id header —
// mirror the active venue into a cookie so the backend resolves it there too.
const setVenueCookie = (id) => {
  if (id) document.cookie = `mise_venue=${id}; path=/; max-age=31536000; samesite=lax`;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [perms, setPerms] = useState({});
  const [activeVenue, setActiveVenue] = useState(() => localStorage.getItem("mise_active_venue") || "");

  const loadPerms = useCallback(() => {
    api.get("/my/permissions").then((r) => setPerms(r.data.permissions || {})).catch(() => setPerms({}));
  }, []);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  // Reconcile the stored venue with what this account can actually access
  useEffect(() => {
    if (user && user.venues && user.venues.length) {
      const stored = localStorage.getItem("mise_active_venue");
      const valid = user.venues.find((v) => v.id === stored);
      const vid = valid ? stored : (user.active_venue_id || user.venues[0].id);
      if (vid !== stored) localStorage.setItem("mise_active_venue", vid);
      setVenueCookie(vid);
      setActiveVenue(vid);
    }
  }, [user]);

  useEffect(() => {
    if (user) loadPerms();
    else setPerms({});
  }, [user, loadPerms]);

  const login = async (email, password) => {
    await api.post("/auth/login", { email, password });
    const { data } = await api.get("/auth/me");
    setUser(data);
    loadPerms();
    return data;
  };

  const switchVenue = (id) => {
    localStorage.setItem("mise_active_venue", id);
    setVenueCookie(id);
    window.location.reload(); // full reload: every screen re-queries scoped to the new venue
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
    setPerms({});
  };

  const can = (k) => user?.role === "owner" || !!perms[k];

  return (
    <AuthCtx.Provider value={{ user, setUser, login, logout, perms, can, loadPerms, activeVenue, switchVenue }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
